import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import {
  FolderPlus,
  Folder,
  FolderOpen,
  Sparkles,
  Download,
  Trash2,
  ExternalLink,
  Tag as TagIcon,
  ArrowRight,
  Clock,
  History as HistoryIcon,
  Play,
  Link as LinkIcon,
  X as XIcon,
} from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { ExportCollectionDialog } from '@/components/ExportCollectionDialog';

/**
 * Ari Collections page — Doctrine v1.0 + Single-Pass Completion Directive.
 *
 * Layout: 3-column responsive
 *   • Left sidebar  → Recent Collections · Recent Exports · Saved Scans
 *   • Middle       → Folder tree (root + subfolders)
 *   • Right main   → Item grid + folder stats + exports
 *
 * All UI actions call real backend endpoints. Nothing is mocked.
 */

const PURPOSES = [
  { value: '', label: 'General' },
  { value: 'appliances', label: 'Appliances / Products' },
  { value: 'recruiting', label: 'Recruiting / Candidates' },
  { value: 'business_leads', label: 'Business Leads' },
  { value: 'jobs', label: 'Jobs / Opportunities' },
];

const STATUS_OPTIONS = [
  'new', 'reviewed', 'interested', 'contacted', 'unavailable', 'sold', 'completed', 'archived',
];

const relTime = (iso) => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const now = Date.now();
  const s = Math.round((now - then) / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
};

const Collections = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  // Aug 2026 doctrine — Ari Search+ retired. Every Zita Pro user (including
  // trial + Super Admin) has full access to Collections. `hasAddon` is
  // retained as a variable name for minimal diff; semantics widened.
  const hasAddon = !!user && (user.has_active_access || user.is_admin);

  const [collections, setCollections] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newFolder, setNewFolder] = useState({ open: false, name: '', purpose: '', parent_id: null });

  // Sidebar payload
  const [recent, setRecent] = useState({ recent_collections: [], recent_exports: [], saved_scans: [] });

  // Google Sheets connect status (OPTIONAL convenience destination)
  const [gStatus, setGStatus] = useState({ configured: false, connected: false });

  // Item selection & export dialog
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [exportOpen, setExportOpen] = useState(false);

  // Saved scan detail
  const [openedScan, setOpenedScan] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/collections');
      setCollections(data.collections || []);
      if (!activeId && data.collections?.length) setActiveId(data.collections[0].id);
    } catch (e) {
      if (e.response?.status === 402) {
        setCollections([]);
      } else {
        toast.error('Could not load collections.');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadRecent = async () => {
    try {
      const { data } = await api.get('/collections/recent');
      setRecent(data);
    } catch { /* ignore */ }
  };

  const loadGStatus = async () => {
    try {
      const { data } = await api.get('/collections/google/status');
      setGStatus(data);
    } catch { /* ignore */ }
  };

  const loadItems = async (id) => {
    if (!id) return;
    try {
      const { data } = await api.get(`/collections/${id}/items?limit=500`);
      setItems(data.items || []);
    } catch (e) {
      toast.error('Could not load items.');
    }
  };

  useEffect(() => { if (hasAddon) { load(); loadRecent(); loadGStatus(); } }, [hasAddon]);
  useEffect(() => { if (hasAddon && activeId) { loadItems(activeId); setSelectedIds(new Set()); } }, [activeId, hasAddon]);

  // OAuth returning from Google callback → show toast
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const g = params.get('google');
    if (g === 'connected') {
      toast.success('Google account connected.');
      loadGStatus();
      // Clean the URL
      navigate('/collections', { replace: true });
    } else if (g === 'error') {
      toast.error('Google connection failed. Try again.');
      navigate('/collections', { replace: true });
    } else if (g === 'configuration_missing') {
      toast.error('Direct Google Sheets sending isn\'t enabled on this deployment. Use the Export dialog to download Excel or CSV.');
      navigate('/collections', { replace: true });
    }
  }, [location.search, navigate]);

  const active = useMemo(
    () => collections.find((c) => c.id === activeId),
    [collections, activeId],
  );

  const createFolder = async () => {
    if (!newFolder.name.trim()) {
      toast.error('Name your collection.');
      return;
    }
    try {
      const { data } = await api.post('/collections', {
        name: newFolder.name.trim(),
        purpose: newFolder.purpose || null,
        parent_id: newFolder.parent_id,
      });
      toast.success('Collection created.');
      setNewFolder({ open: false, name: '', purpose: '', parent_id: null });
      await load();
      await loadRecent();
      setActiveId(data.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not create.');
    }
  };

  const exportOpenSuccess = () => {
    setExportOpen(false);
    loadRecent();  // Refresh Recent Exports sidebar
  };

  const deleteFolder = async () => {
    if (!active) return;
    if (!window.confirm(`Delete "${active.name}"? Contained items will be archived.`)) return;
    try {
      await api.delete(`/collections/${active.id}?archive_items=true`);
      toast.success('Collection deleted.');
      setActiveId(null);
      setItems([]);
      await load();
      loadRecent();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not delete.');
    }
  };

  const updateItem = async (id, patch) => {
    try {
      await api.patch(`/collections/items/${id}`, patch);
      setItems((xs) => xs.map((it) => (it.id === id ? { ...it, ...patch } : it)));
    } catch {
      toast.error('Could not update.');
    }
  };

  const removeItem = async (id) => {
    try {
      await api.delete(`/collections/items/${id}`);
      setItems((xs) => xs.filter((it) => it.id !== id));
      load();
    } catch {
      toast.error('Could not remove.');
    }
  };

  // Saved scan actions
  const openSavedScan = async (id) => {
    try {
      const { data } = await api.get(`/collections/saved-scans/${id}`);
      setOpenedScan(data);
    } catch { toast.error('Could not open saved scan.'); }
  };

  const runSavedScanAgain = async (id) => {
    try {
      const { data } = await api.post(`/collections/saved-scans/${id}/run`);
      toast.success('New scan queued.');
      navigate(`/results/${data.scan_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not run again.');
    }
  };

  const deleteSavedScan = async (id) => {
    if (!window.confirm('Delete this saved scan? The original scan history is unaffected.')) return;
    try {
      await api.delete(`/collections/saved-scans/${id}`);
      toast.success('Saved scan deleted.');
      loadRecent();
      setOpenedScan(null);
    } catch { toast.error('Could not delete.'); }
  };

  if (!hasAddon) return <Paywall />;

  const rootFolders = collections.filter((c) => !c.parent_id);

  return (
    <div className="grid lg:grid-cols-[260px_240px_1fr] md:grid-cols-[220px_1fr] gap-5 max-w-[1400px] mx-auto px-4 sm:px-8 py-6">
      {/* LEFT SIDEBAR — Recent activity */}
      <aside className="lg:block hidden space-y-5" data-testid="collections-recent-sidebar">
        <SidebarSection title="Recent Collections" icon={FolderOpen}>
          {recent.recent_collections.length === 0 ? (
            <Empty text="No recent activity." />
          ) : (
            recent.recent_collections.map((c) => (
              <button
                key={c.id}
                type="button"
                data-testid={`recent-collection-${c.id}`}
                onClick={() => setActiveId(c.id)}
                className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-[hsl(var(--zita-silver-soft))] text-sm zita-ink-soft truncate flex items-center gap-2"
              >
                <Folder size={13} className="zita-ink-muted shrink-0" />
                <span className="truncate">{c.name}</span>
              </button>
            ))
          )}
        </SidebarSection>

        <SidebarSection title="Recent Exports" icon={Download}>
          {recent.recent_exports.length === 0 ? (
            <Empty text="No exports yet." />
          ) : (
            recent.recent_exports.map((ex) => (
              <div key={ex.id} className="px-2 py-1.5 rounded-lg hover:bg-[hsl(var(--zita-silver-soft))]" data-testid={`recent-export-${ex.id}`}>
                <div className="flex items-center gap-2 text-sm zita-ink-soft truncate">
                  <Download size={12} className="zita-ink-muted shrink-0" />
                  <span className="truncate">{ex.collection_name}</span>
                </div>
                <div className="flex items-center gap-2 text-[10px] zita-ink-muted uppercase tracking-wider mt-0.5">
                  <span>{ex.format}</span>
                  <span>· {ex.item_count} rows</span>
                  <span>· {relTime(ex.created_at)}</span>
                  {ex.result_url && (
                    <a href={ex.result_url} target="_blank" rel="noopener noreferrer" className="zita-blue-text hover:underline inline-flex items-center gap-1">
                      <LinkIcon size={10} /> open
                    </a>
                  )}
                </div>
              </div>
            ))
          )}
        </SidebarSection>

        <SidebarSection title="Saved Scans" icon={HistoryIcon}>
          {recent.saved_scans.length === 0 ? (
            <Empty text="Save a scan from the Results page." />
          ) : (
            recent.saved_scans.map((s) => (
              <button
                key={s.id}
                type="button"
                data-testid={`saved-scan-${s.id}`}
                onClick={() => openSavedScan(s.id)}
                className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-[hsl(var(--zita-silver-soft))]"
              >
                <div className="flex items-center gap-2 text-sm zita-ink-soft truncate">
                  <Clock size={12} className="zita-ink-muted shrink-0" />
                  <span className="truncate">{s.name || s.command_text || 'Saved scan'}</span>
                </div>
                <div className="text-[10px] zita-ink-muted uppercase tracking-wider mt-0.5">
                  {s.total_listings} items · {relTime(s.saved_at)}
                </div>
              </button>
            ))
          )}
        </SidebarSection>
      </aside>

      {/* MIDDLE — Folder tree */}
      <aside className="space-y-3">
        <div className="flex items-center justify-between">
          <h1 className="font-serif text-2xl zita-ink">Collections</h1>
          <button
            type="button"
            onClick={() => setNewFolder({ open: true, name: '', purpose: '', parent_id: null })}
            data-testid="collections-new"
            className="p-2 rounded-full hover:bg-[hsl(var(--zita-silver-soft))]"
            aria-label="New collection"
          >
            <FolderPlus size={18} className="ari-accent" />
          </button>
        </div>

        {newFolder.open && (
          <div className="zita-card p-3 space-y-2">
            <input
              autoFocus
              value={newFolder.name}
              onChange={(e) => setNewFolder({ ...newFolder, name: e.target.value })}
              placeholder="Collection name"
              data-testid="collections-new-name"
              className="zita-input w-full px-3 py-2 text-sm"
            />
            <select
              value={newFolder.purpose}
              onChange={(e) => setNewFolder({ ...newFolder, purpose: e.target.value })}
              data-testid="collections-new-purpose"
              className="zita-input w-full px-3 py-2 text-sm"
            >
              {PURPOSES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
            {rootFolders.length > 0 && (
              <select
                value={newFolder.parent_id || ''}
                onChange={(e) => setNewFolder({ ...newFolder, parent_id: e.target.value || null })}
                data-testid="collections-new-parent"
                className="zita-input w-full px-3 py-2 text-xs"
              >
                <option value="">Top-level (root folder)</option>
                {rootFolders.map((c) => (
                  <option key={c.id} value={c.id}>Inside: {c.name}</option>
                ))}
              </select>
            )}
            <div className="flex items-center gap-2">
              <button type="button" onClick={createFolder} data-testid="collections-new-submit" className="zita-btn-primary text-xs !py-1.5 !px-3">Create</button>
              <button type="button" onClick={() => setNewFolder({ open: false, name: '', purpose: '', parent_id: null })} className="text-xs zita-ink-muted">Cancel</button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-sm zita-ink-muted">Loading…</div>
        ) : rootFolders.length === 0 ? (
          <p className="text-sm zita-ink-muted">
            No collections yet. Create one to organize what Ari finds.
          </p>
        ) : (
          <div className="space-y-1">
            {rootFolders.map((c) => {
              const subs = collections.filter((s) => s.parent_id === c.id);
              return (
                <div key={c.id}>
                  <FolderRow
                    folder={c}
                    active={activeId === c.id}
                    onSelect={setActiveId}
                  />
                  {subs.length > 0 && (
                    <div className="ml-4 space-y-1">
                      {subs.map((s) => (
                        <FolderRow
                          key={s.id}
                          folder={s}
                          active={activeId === s.id}
                          onSelect={setActiveId}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </aside>

      {/* RIGHT — item grid + stats */}
      <section className="min-w-0">
        {active ? (
          <>
            <header className="flex flex-wrap items-start justify-between gap-3 mb-4">
              <div className="min-w-0">
                <h2 className="font-serif text-3xl zita-ink truncate">{active.name}</h2>
                <div className="text-xs zita-ink-muted mt-1">
                  {PURPOSES.find((p) => p.value === (active.purpose || ''))?.label}
                  {active.last_updated && <span> · Updated {relTime(active.last_updated)}</span>}
                </div>
                {active.stats && (
                  <div className="mt-2 flex flex-wrap gap-2 text-[11px]" data-testid="collection-stats">
                    <Chip label={`${active.stats.total} Items`} strong />
                    <Chip label={`${active.stats.new} New`} tone="blue" />
                    <Chip label={`${active.stats.contacted} Contacted`} tone="amber" />
                    <Chip label={`${active.stats.sold} Sold`} tone="green" />
                    {active.stats.interested > 0 && <Chip label={`${active.stats.interested} Interested`} tone="purple" />}
                    {active.stats.archived > 0 && <Chip label={`${active.stats.archived} Archived`} tone="gray" />}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={() => setExportOpen(true)}
                  data-testid="collections-export-open"
                  className="zita-btn-primary text-sm inline-flex items-center gap-1"
                >
                  <Download size={13} /> Export…
                </button>
                {selectedIds.size > 0 && (
                  <span className="text-[11px] zita-ink-muted" data-testid="collections-selected-count">
                    {selectedIds.size} selected
                  </span>
                )}
                <button
                  type="button"
                  onClick={deleteFolder}
                  data-testid="collections-delete"
                  className="p-2 rounded-full hover:bg-red-50 text-red-600"
                  aria-label="Delete collection"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </header>

            {items.length === 0 ? (
              <div className="zita-card p-8 text-center">
                <div className="text-sm zita-ink-muted">
                  Nothing saved yet. Save opportunities from Alerts, Results, Home, or Vault to this collection.
                </div>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
                {items.map((it) => (
                  <ItemCard
                    key={it.id}
                    item={it}
                    selected={selectedIds.has(it.id)}
                    onToggleSelect={() => {
                      setSelectedIds((s) => {
                        const n = new Set(s);
                        n.has(it.id) ? n.delete(it.id) : n.add(it.id);
                        return n;
                      });
                    }}
                    onStatus={(status) => updateItem(it.id, { status })}
                    onDelete={() => removeItem(it.id)}
                  />
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="zita-card p-8 text-center">
            <div className="text-sm zita-ink-muted">Select a collection to see saved cards.</div>
          </div>
        )}
      </section>

      {openedScan && (
        <SavedScanModal
          scan={openedScan}
          onClose={() => setOpenedScan(null)}
          onRunAgain={runSavedScanAgain}
          onDelete={deleteSavedScan}
        />
      )}
      {exportOpen && active && (
        <ExportCollectionDialog
          collection={active}
          selectedIds={selectedIds}
          onClose={exportOpenSuccess}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const SidebarSection = ({ title, icon: Icon, children }) => (
  <div>
    <div className="text-[10px] font-semibold uppercase tracking-[0.14em] zita-ink-muted flex items-center gap-1 mb-2">
      <Icon size={11} /> {title}
    </div>
    <div className="space-y-1">{children}</div>
  </div>
);

const Empty = ({ text }) => (
  <div className="text-[11px] zita-ink-muted italic px-2 py-1">{text}</div>
);

const Chip = ({ label, strong, tone }) => {
  const map = {
    blue: 'bg-[hsl(var(--zita-blue-soft))] zita-blue-text',
    amber: 'bg-orange-50 text-orange-700',
    green: 'bg-emerald-50 text-emerald-700',
    purple: 'bg-[hsl(var(--ari-purple-soft))] ari-accent',
    gray: 'bg-[hsl(var(--zita-silver-soft))] zita-ink-muted',
  };
  const klass = strong
    ? 'bg-[hsl(var(--zita-ink))] text-white font-semibold'
    : (map[tone] || 'bg-[hsl(var(--zita-silver-soft))] zita-ink-soft');
  return (
    <span className={`px-2 py-0.5 rounded-full ${klass}`}>{label}</span>
  );
};

const FolderRow = ({ folder, active, onSelect }) => {
  const Icon = active ? FolderOpen : Folder;
  return (
    <button
      type="button"
      onClick={() => onSelect(folder.id)}
      data-testid={`collection-row-${folder.id}`}
      className={`w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-left ${
        active
          ? 'bg-[hsl(var(--zita-blue-soft))] zita-blue-text font-semibold'
          : 'zita-ink-soft hover:bg-[hsl(var(--zita-silver-soft))]'
      }`}
    >
      <Icon size={16} className={active ? 'zita-blue-text' : 'zita-ink-muted'} />
      <span className="flex-1 truncate">{folder.name}</span>
      <span className="text-xs zita-ink-muted">{folder.item_count}</span>
    </button>
  );
};

const ItemCard = ({ item, selected, onToggleSelect, onStatus, onDelete }) => (
  <div
    className={`zita-card p-4 flex flex-col gap-2 transition ${selected ? 'ring-2 ring-[color:var(--zita-blue)]' : ''}`}
    data-testid={`collection-item-${item.id}`}
  >
    <div className="flex items-start justify-between gap-2">
      <label className="flex items-start gap-2 flex-1 min-w-0 cursor-pointer">
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggleSelect}
          data-testid={`item-select-${item.id}`}
          className="mt-1 shrink-0"
          aria-label={`Select ${item.title}`}
        />
        <h3 className="font-serif text-base zita-ink truncate">{item.title}</h3>
      </label>
      <button type="button" onClick={onDelete} aria-label="Remove" data-testid={`item-remove-${item.id}`} className="p-1 rounded hover:bg-red-50 text-red-500">
        <Trash2 size={13} />
      </button>
    </div>
    <div className="text-xs zita-ink-muted flex flex-wrap gap-x-3">
      {item.price !== null && item.price !== undefined && (
        <span className="zita-orange-text font-semibold">{item.is_free ? 'FREE' : `$${item.price}`}</span>
      )}
      {item.location_text && <span>{item.location_text}</span>}
      {typeof item.distance_miles === 'number' && <span>{item.distance_miles.toFixed(0)} mi</span>}
      {item.source_name && <span>· {item.source_name}</span>}
    </div>
    {item.search_term && (
      <div className="text-[10px] zita-ink-muted italic truncate">
        via search: &ldquo;{item.search_term}&rdquo;
      </div>
    )}
    {item.date_found && (
      <div className="text-[10px] zita-ink-muted">Found: {item.date_found?.slice(0, 10)} · Saved: {item.date_saved?.slice(0, 10)}</div>
    )}
    {item.notes && <p className="text-xs zita-ink-soft italic line-clamp-2">{item.notes}</p>}
    {item.tags?.length > 0 && (
      <div className="flex flex-wrap gap-1">
        {item.tags.map((t) => (
          <span key={t} className="text-[10px] px-2 py-0.5 rounded-full bg-[hsl(var(--zita-silver-soft))] zita-ink-soft inline-flex items-center gap-1">
            <TagIcon size={9} /> {t}
          </span>
        ))}
      </div>
    )}
    <div className="mt-1 flex flex-wrap items-center gap-2">
      {item.external_url && (
        <a
          href={item.external_url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`item-external-${item.id}`}
          className="text-xs zita-blue-text hover:underline inline-flex items-center gap-1"
        >
          <ExternalLink size={11} /> Open original
        </a>
      )}
      <select
        value={item.status}
        onChange={(e) => onStatus(e.target.value)}
        data-testid={`collection-item-${item.id}-status`}
        className="text-xs px-2 py-1 rounded-md bg-[hsl(var(--zita-silver-soft))] zita-ink-soft"
      >
        {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
    </div>
  </div>
);

const SavedScanModal = ({ scan, onClose, onRunAgain, onDelete }) => (
  <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" role="dialog" aria-label="Saved scan detail" data-testid="saved-scan-modal">
    <button type="button" aria-label="Close" onClick={onClose} className="absolute inset-0 bg-black/45 backdrop-blur-sm" />
    <div className="relative w-full max-w-2xl zita-card p-6 sm:p-7 zita-fade-up max-h-[90vh] overflow-y-auto zita-scroll">
      <button type="button" onClick={onClose} className="absolute top-3 right-3 p-2 rounded-full hover:bg-[hsl(var(--zita-silver-soft))] zita-ink-muted" aria-label="Close">
        <XIcon size={18} />
      </button>
      <div className="text-xs uppercase tracking-[0.16em] zita-ink-muted">Saved scan · frozen snapshot</div>
      <h2 className="font-serif text-2xl zita-ink mt-1">{scan.name || scan.command_text || 'Saved scan'}</h2>
      <div className="mt-2 flex flex-wrap gap-3 text-xs zita-ink-muted">
        <span>Search: <span className="zita-ink-soft">&ldquo;{scan.search_term || scan.command_text}&rdquo;</span></span>
        <span>· Saved {relTime(scan.saved_at)}</span>
        <span>· {scan.total_listings} matches</span>
        <span>· {(scan.sources_scanned || []).length} sources</span>
      </div>
      {scan.filters && (
        <div className="mt-3 text-[11px] zita-ink-muted">
          <span className="uppercase tracking-widest">Filters:</span>{' '}
          {scan.filters.location_text && <span>{scan.filters.location_text} · </span>}
          {scan.filters.radius_miles && <span>{scan.filters.radius_miles} mi radius · </span>}
          {scan.filters.max_price != null && <span>≤${scan.filters.max_price} · </span>}
          {scan.filters.free_only && <span>Free only · </span>}
          {scan.filters.search_type && <span>{scan.filters.search_type}</span>}
        </div>
      )}
      {(scan.source_failures || []).length > 0 && (
        <div className="mt-2 text-[11px] text-red-600">
          Failures: {scan.source_failures.map((f) => f.display_name).join(', ')}
        </div>
      )}
      <div className="mt-4 flex flex-wrap gap-2">
        <button type="button" data-testid="saved-scan-run-again" onClick={() => onRunAgain(scan.id)} className="zita-btn-primary text-sm inline-flex items-center gap-1">
          <Play size={12} /> Run this search again
        </button>
        <button type="button" data-testid="saved-scan-delete" onClick={() => onDelete(scan.id)} className="zita-btn-secondary text-sm inline-flex items-center gap-1 text-red-600">
          <Trash2 size={12} /> Delete
        </button>
      </div>
      <div className="mt-6 space-y-2">
        <div className="text-xs uppercase tracking-widest zita-ink-muted">Frozen listings</div>
        {(scan.listings || []).slice(0, 30).map((l, idx) => (
          <div key={idx} className="flex items-start gap-3 border border-[color:var(--zita-hairline)] rounded-xl p-3">
            {l.photos?.[0] && (
              <img src={l.photos[0]} alt="" className="w-14 h-14 rounded-lg object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
            )}
            <div className="flex-1 min-w-0">
              <div className="text-sm zita-ink font-medium truncate">{l.title}</div>
              <div className="text-[11px] zita-ink-muted">
                {l.is_free ? 'FREE' : (l.price != null ? `$${l.price}` : '')}
                {l.location_text && ` · ${l.location_text}`}
                {l.source_name && ` · ${l.source_name}`}
              </div>
            </div>
            {l.external_url && (
              <a href={l.external_url} target="_blank" rel="noopener noreferrer" className="text-xs zita-blue-text hover:underline inline-flex items-center gap-1">
                <ExternalLink size={11} /> Open
              </a>
            )}
          </div>
        ))}
        {(scan.listings || []).length > 30 && (
          <div className="text-[11px] zita-ink-muted italic">…and {scan.listings.length - 30} more preserved in the snapshot.</div>
        )}
      </div>
    </div>
  </div>
);

const Paywall = () => (
  <div className="max-w-3xl mx-auto px-4 sm:px-8 py-10">
    <div className="zita-card p-8">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-10 h-10 rounded-full zita-ari-avatar flex items-center justify-center">
          <Sparkles size={16} />
        </div>
        <div className="text-xs uppercase tracking-[0.14em] zita-ink-muted">Collections · Included in Zita Pro</div>
      </div>
      <h1 className="font-serif text-4xl zita-ink leading-tight">
        Organize what Ari finds — and keep it forever.
      </h1>
      <p className="mt-4 text-base zita-ink-soft leading-relaxed max-w-2xl">
        Collections is part of Zita Pro at $29.99 / month. Save individual
        opportunities or every match from a scan, organize into unlimited
        folders + subfolders, keep the search context frozen, and export as
        Excel, CSV, PDF, or Google Sheets. Ari can even suggest folder names
        based on what you save.
      </p>
      <ul className="mt-6 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm zita-ink-soft">
        <li>· Unlimited folders and subfolders</li>
        <li>· Card fields adapt to the search type</li>
        <li>· Original source URL always preserved</li>
        <li>· Duplicate detection across sources</li>
        <li>· Save all matches in one click</li>
        <li>· Save entire scans as frozen snapshots</li>
        <li>· Run This Search Again from any snapshot</li>
        <li>· Excel, CSV, PDF, and Google Sheets export</li>
      </ul>
      <div className="mt-8 flex flex-wrap items-center gap-3">
        <a
          href="/subscribe"
          data-testid="collections-paywall-cta"
          className="zita-btn-primary inline-flex items-center gap-2"
        >
          Start your 3-day free trial <ArrowRight size={14} />
        </a>
        <a href="/billing" className="text-sm zita-blue-text hover:underline">See pricing</a>
      </div>
      <p className="mt-6 text-[11px] zita-ink-muted">
        Collections is a research and organization tool. It doesn&apos;t
        contact anyone on your behalf. Every result links back to the
        original public source.
      </p>
    </div>
  </div>
);

export default Collections;
